import type { Editor } from '@tiptap/core';
import { createStore, useStore, type StoreApi } from 'zustand';

/** Text range a new comment will be attached to once it is posted. */
export type CommentDraft = {
    /** New for every started comment, and kept while the range moves with edits. */
    id: number;
    from: number;
    to: number;
};

let nextDraftId = 1;

type CommentDraftState = {
    draft: CommentDraft | null;
};

const editorStores = new WeakMap<Editor, StoreApi<CommentDraftState>>();
const EMPTY_STORE = createStore<CommentDraftState>(() => ({ draft: null }));

function getCommentDraftStore(editor: Editor): StoreApi<CommentDraftState> {
    let store = editorStores.get(editor);
    if (!store) {
        store = createStore<CommentDraftState>(() => ({ draft: null }));
        editorStores.set(editor, store);
    }
    return store;
}

/**
 * Starts a comment on the current selection. Nothing is written to the document until the draft
 * is posted, so a cancelled draft leaves no empty thread behind.
 */
export function startCommentDraft(editor: Editor): boolean {
    const { from, to, empty } = editor.state.selection;
    if (empty) {
        return false;
    }
    getCommentDraftStore(editor).setState({ draft: { id: nextDraftId++, from, to } });
    return true;
}

export function setCommentDraft(editor: Editor, draft: CommentDraft | null): void {
    getCommentDraftStore(editor).setState({ draft });
}

export function useCommentDraft(editor: Editor | null): CommentDraft | null {
    // Subscribing to a throwaway store keeps the hook call unconditional while the editor is loading.
    const store = editor ? getCommentDraftStore(editor) : EMPTY_STORE;
    return useStore(store, state => state.draft);
}
